import React, { useState, useEffect } from 'react';
import {
    AiOutlinePlus,
    AiOutlineSearch,
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineDownload,
    AiOutlinePrinter,
    AiOutlineGlobal,
    AiOutlineClockCircle,
    AiOutlineCheckCircle,
    AiOutlineExclamationCircle,
    AiOutlineCalendar,
    AiOutlineEye,
    AiOutlineDollar,
    AiOutlineShopping,
    AiOutlineSetting,
    AiOutlineReload,
} from 'react-icons/ai';
import styles from './BuyDomain.module.css';

function BuyDomain() {
    const [domains, setDomains] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [showRenewModal, setShowRenewModal] = useState(false);

    // Sample data - replace with actual API calls
    const sampleDomains = [
        {
            id: 1,
            domainName: 'example.com',
            registrar: 'GoDaddy',
            registrationDate: '2023-01-15',
            expiryDate: '2024-01-15',
            status: 'active',
            price: 12.99,
            autoRenew: true,
            dnsServers: ['ns1.godaddy.com', 'ns2.godaddy.com'],
        },
        {
            id: 2,
            domainName: 'mystore.com',
            registrar: 'Namecheap',
            registrationDate: '2023-03-20',
            expiryDate: '2024-03-20',
            status: 'expiring',
            price: 8.99,
            autoRenew: false,
            dnsServers: [
                'dns1.registrar-servers.com',
                'dns2.registrar-servers.com',
            ],
        },
        {
            id: 3,
            domainName: 'business.org',
            registrar: 'Cloudflare',
            registrationDate: '2023-06-10',
            expiryDate: '2024-06-10',
            status: 'active',
            price: 9.99,
            autoRenew: true,
            dnsServers: ['ns1.cloudflare.com', 'ns2.cloudflare.com'],
        },
        {
            id: 4,
            domainName: 'shop.net',
            registrar: 'Name.com',
            registrationDate: '2023-09-05',
            expiryDate: '2024-09-05',
            status: 'expired',
            price: 11.99,
            autoRenew: false,
            dnsServers: ['ns1.name.com', 'ns2.name.com'],
        },
    ];

    useEffect(() => {
        setDomains(sampleDomains);
    }, []);

    const formatCurrency = (amount) => {
        return `৳${amount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            active: styles.statusActive,
            expiring: styles.statusExpiring,
            expired: styles.statusExpired,
            suspended: styles.statusSuspended,
        };
        return `${styles.statusBadge} ${
            statusClasses[status] || styles.statusActive
        }`;
    };

    const getDaysUntilExpiry = (expiryDate) => {
        const today = new Date();
        const expiry = new Date(expiryDate);
        const diffTime = expiry - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const filteredDomains = domains.filter((domain) => {
        const matchesSearch =
            domain.domainName
                .toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
            domain.registrar.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus =
            filterStatus === 'all' || domain.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const getTotalDomains = () => domains.length;
    const getActiveDomains = () =>
        domains.filter((domain) => domain.status === 'active').length;
    const getExpiringDomains = () =>
        domains.filter((domain) => domain.status === 'expiring').length;
    const getTotalCost = () =>
        domains.reduce((sum, domain) => sum + domain.price, 0);

    return (
        <div className={styles.domainContainer}>
            {/* Header Section */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.pageTitle}>Domain Management</h1>
                    <p className={styles.pageSubtitle}>
                        Manage your domain registrations and renewals
                    </p>
                </div>
                <div className={styles.headerRight}>
                    <button
                        className={styles.primaryButton}
                        onClick={() => setShowAddModal(true)}
                    >
                        <AiOutlinePlus className={styles.buttonIcon} />
                        Buy New Domain
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineGlobal />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {getTotalDomains()}
                        </div>
                        <div className={styles.statLabel}>Total Domains</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineCheckCircle />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {getActiveDomains()}
                        </div>
                        <div className={styles.statLabel}>Active</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineExclamationCircle />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {getExpiringDomains()}
                        </div>
                        <div className={styles.statLabel}>Expiring Soon</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineDollar />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {formatCurrency(getTotalCost())}
                        </div>
                        <div className={styles.statLabel}>Total Cost</div>
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className={styles.filtersSection}>
                <div className={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="Search domains..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                    <AiOutlineSearch className={styles.searchIcon} />
                </div>
                <div className={styles.filterButtons}>
                    <button
                        className={`${styles.filterButton} ${
                            filterStatus === 'all' ? styles.filterActive : ''
                        }`}
                        onClick={() => setFilterStatus('all')}
                    >
                        All
                    </button>
                    <button
                        className={`${styles.filterButton} ${
                            filterStatus === 'active' ? styles.filterActive : ''
                        }`}
                        onClick={() => setFilterStatus('active')}
                    >
                        Active
                    </button>
                    <button
                        className={`${styles.filterButton} ${
                            filterStatus === 'expiring'
                                ? styles.filterActive
                                : ''
                        }`}
                        onClick={() => setFilterStatus('expiring')}
                    >
                        Expiring
                    </button>
                    <button
                        className={`${styles.filterButton} ${
                            filterStatus === 'expired'
                                ? styles.filterActive
                                : ''
                        }`}
                        onClick={() => setFilterStatus('expired')}
                    >
                        Expired
                    </button>
                    <button className={styles.actionButton}>
                        <AiOutlineDownload className={styles.actionIcon} />
                        Export
                    </button>
                    <button className={styles.actionButton}>
                        <AiOutlinePrinter className={styles.actionIcon} />
                        Print
                    </button>
                </div>
            </div>

            {/* Domains Table */}
            <div className={styles.tableContainer}>
                <div className={styles.tableHeader}>
                    <div className={styles.tableTitle}>Domain Portfolio</div>
                    <div className={styles.tableActions}>
                        <button
                            className={styles.addButton}
                            onClick={() => setShowAddModal(true)}
                        >
                            <AiOutlinePlus className={styles.actionIcon} />
                            Buy Domain
                        </button>
                    </div>
                </div>

                <div className={styles.tableWrapper}>
                    <table className={styles.domainTable}>
                        <thead>
                            <tr>
                                <th>Domain Name</th>
                                <th>Registrar</th>
                                <th>Registration Date</th>
                                <th>Expiry Date</th>
                                <th>Status</th>
                                <th>Price</th>
                                <th>Auto Renew</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDomains.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className={styles.emptyRow}>
                                        <div className={styles.emptyState}>
                                            <div className={styles.emptyIcon}>
                                                <AiOutlineGlobal />
                                            </div>
                                            <h3 className={styles.emptyTitle}>
                                                No domains found
                                            </h3>
                                            <p
                                                className={
                                                    styles.emptyDescription
                                                }
                                            >
                                                {searchTerm
                                                    ? 'Try adjusting your search criteria'
                                                    : 'Get started by purchasing your first domain'}
                                            </p>
                                            {!searchTerm && (
                                                <button
                                                    className={
                                                        styles.emptyActionButton
                                                    }
                                                    onClick={() =>
                                                        setShowAddModal(true)
                                                    }
                                                >
                                                    Buy Domain
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredDomains.map((domain) => (
                                    <tr
                                        key={domain.id}
                                        className={styles.tableRow}
                                    >
                                        <td className={styles.domainCell}>
                                            <div className={styles.domainName}>
                                                {domain.domainName}
                                            </div>
                                        </td>
                                        <td className={styles.registrarCell}>
                                            <div
                                                className={styles.registrarName}
                                            >
                                                {domain.registrar}
                                            </div>
                                        </td>
                                        <td className={styles.dateCell}>
                                            <div className={styles.dateValue}>
                                                {formatDate(
                                                    domain.registrationDate
                                                )}
                                            </div>
                                        </td>
                                        <td className={styles.dateCell}>
                                            <div className={styles.dateValue}>
                                                {formatDate(domain.expiryDate)}
                                            </div>
                                            <div
                                                className={styles.daysRemaining}
                                            >
                                                {getDaysUntilExpiry(
                                                    domain.expiryDate
                                                )}{' '}
                                                days left
                                            </div>
                                        </td>
                                        <td className={styles.statusCell}>
                                            <span
                                                className={getStatusBadge(
                                                    domain.status
                                                )}
                                            >
                                                {domain.status}
                                            </span>
                                        </td>
                                        <td className={styles.priceCell}>
                                            <div className={styles.priceValue}>
                                                {formatCurrency(domain.price)}
                                            </div>
                                        </td>
                                        <td className={styles.renewCell}>
                                            <div className={styles.autoRenew}>
                                                {domain.autoRenew ? (
                                                    <span
                                                        className={
                                                            styles.renewActive
                                                        }
                                                    >
                                                        <AiOutlineCheckCircle />
                                                        Enabled
                                                    </span>
                                                ) : (
                                                    <span
                                                        className={
                                                            styles.renewInactive
                                                        }
                                                    >
                                                        <AiOutlineExclamationCircle />
                                                        Disabled
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className={styles.actionsCell}>
                                            <div
                                                className={styles.actionButtons}
                                            >
                                                <button
                                                    className={
                                                        styles.viewButton
                                                    }
                                                    onClick={() =>
                                                        setSelectedDomain(
                                                            domain
                                                        )
                                                    }
                                                    title="View Details"
                                                >
                                                    <AiOutlineEye />
                                                </button>
                                                <button
                                                    className={
                                                        styles.editButton
                                                    }
                                                    onClick={() =>
                                                        setShowRenewModal(true)
                                                    }
                                                    title="Renew Domain"
                                                >
                                                    <AiOutlineReload />
                                                </button>
                                                <button
                                                    className={
                                                        styles.settingButton
                                                    }
                                                    title="DNS Settings"
                                                >
                                                    <AiOutlineSetting />
                                                </button>
                                                <button
                                                    className={
                                                        styles.deleteButton
                                                    }
                                                    title="Delete"
                                                >
                                                    <AiOutlineDelete />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Domain Detail Modal */}
            {selectedDomain && (
                <div className={styles.domainModal}>
                    <div className={styles.modalContainer}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>
                                {selectedDomain.domainName}
                            </h3>
                            <button
                                className={styles.closeButton}
                                onClick={() => setSelectedDomain(null)}
                            >
                                ×
                            </button>
                        </div>
                        <div className={styles.modalContent}>
                            <div className={styles.domainInfo}>
                                <div className={styles.infoSection}>
                                    <h4 className={styles.sectionTitle}>
                                        Domain Information
                                    </h4>
                                    <div className={styles.infoGrid}>
                                        <div className={styles.infoItem}>
                                            <span className={styles.infoLabel}>
                                                Domain Name:
                                            </span>
                                            <span className={styles.infoValue}>
                                                {selectedDomain.domainName}
                                            </span>
                                        </div>
                                        <div className={styles.infoItem}>
                                            <span className={styles.infoLabel}>
                                                Registrar:
                                            </span>
                                            <span className={styles.infoValue}>
                                                {selectedDomain.registrar}
                                            </span>
                                        </div>
                                        <div className={styles.infoItem}>
                                            <span className={styles.infoLabel}>
                                                Registration Date:
                                            </span>
                                            <span className={styles.infoValue}>
                                                {formatDate(
                                                    selectedDomain.registrationDate
                                                )}
                                            </span>
                                        </div>
                                        <div className={styles.infoItem}>
                                            <span className={styles.infoLabel}>
                                                Expiry Date:
                                            </span>
                                            <span className={styles.infoValue}>
                                                {formatDate(
                                                    selectedDomain.expiryDate
                                                )}
                                            </span>
                                        </div>
                                        <div className={styles.infoItem}>
                                            <span className={styles.infoLabel}>
                                                Status:
                                            </span>
                                            <span
                                                className={getStatusBadge(
                                                    selectedDomain.status
                                                )}
                                            >
                                                {selectedDomain.status}
                                            </span>
                                        </div>
                                        <div className={styles.infoItem}>
                                            <span className={styles.infoLabel}>
                                                Price:
                                            </span>
                                            <span className={styles.infoValue}>
                                                {formatCurrency(
                                                    selectedDomain.price
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.infoSection}>
                                    <h4 className={styles.sectionTitle}>
                                        DNS Servers
                                    </h4>
                                    <div className={styles.dnsList}>
                                        {selectedDomain.dnsServers.map(
                                            (dns, index) => (
                                                <div
                                                    key={index}
                                                    className={styles.dnsItem}
                                                >
                                                    <span
                                                        className={
                                                            styles.dnsNumber
                                                        }
                                                    >
                                                        NS{index + 1}:
                                                    </span>
                                                    <span
                                                        className={
                                                            styles.dnsValue
                                                        }
                                                    >
                                                        {dns}
                                                    </span>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.domainActions}>
                                <button className={styles.renewButton}>
                                    <AiOutlineReload
                                        className={styles.buttonIcon}
                                    />
                                    Renew Domain
                                </button>
                                <button className={styles.dnsButton}>
                                    <AiOutlineSetting
                                        className={styles.buttonIcon}
                                    />
                                    Manage DNS
                                </button>
                                <button className={styles.transferButton}>
                                    <AiOutlineShopping
                                        className={styles.buttonIcon}
                                    />
                                    Transfer Domain
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BuyDomain;
